import { FormEvent, useEffect, useState } from "react";
import { CheckCircle } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const CONTACT_SALES_AREAS_OF_INTEREST = [
  "License Compliance",
  "Security Vulnerabilities",
  "SBOM & Dependency Management",
  "Encryption / PQC Readiness",
  "General Overview",
] as const;

export interface ContactSalesFormData {
  fullName: string;
  organisationName: string;
  workEmail: string;
  areaOfInterest: string;
  message?: string;
}

interface ContactSalesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Explicit dialog title. Falls back to a planName-based title, then a generic one. */
  title?: string;
  planName?: string;
  /** Pre-fills for a signed-in caller (e.g. the billing page); left blank for anonymous visitors. */
  defaultFullName?: string;
  defaultOrganisationName?: string;
  defaultWorkEmail?: string;
  /**
   * Show and require the Organisation Name field. The landing-page demo form
   * passes false to keep its original four fields; the backend's public DTO
   * treats organisationName as optional to match.
   */
  showOrganisation?: boolean;
  /**
   * On success, show the in-dialog checkmark confirmation instead of a success
   * toast. The public marketing pages use this -- it's a stronger confirmation
   * on a conversion surface, and it doesn't depend on a <Toaster> being
   * mounted. Errors still toast either way.
   */
  confirmOnSuccess?: boolean;
  /**
   * Real submission handler (e.g. wired to a backend endpoint). When omitted, the form
   * falls back to a local-only "we'll be in touch" confirmation.
   */
  onSubmit?: (data: ContactSalesFormData) => Promise<void>;
}

const ContactSalesModal = ({
  open,
  onOpenChange,
  title,
  planName,
  defaultFullName = "",
  defaultOrganisationName = "",
  defaultWorkEmail = "",
  showOrganisation = true,
  confirmOnSuccess = false,
  onSubmit,
}: ContactSalesModalProps) => {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fullName, setFullName] = useState(defaultFullName);
  const [organisationName, setOrganisationName] = useState(defaultOrganisationName);
  const [workEmail, setWorkEmail] = useState(defaultWorkEmail);
  const [areaOfInterest, setAreaOfInterest] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (open) {
      setFullName(defaultFullName);
      setOrganisationName(defaultOrganisationName);
      setWorkEmail(defaultWorkEmail);
      setAreaOfInterest("");
      setMessage("");
    }
  }, [open, defaultFullName, defaultOrganisationName, defaultWorkEmail]);

  const handleClose = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) setSubmitted(false);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!areaOfInterest) {
      toast.error("Please select an area of interest.");
      return;
    }

    if (!onSubmit) {
      setSubmitted(true);
      window.setTimeout(() => handleClose(false), 2500);
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({ fullName, organisationName, workEmail, areaOfInterest, message: message || undefined });
      if (confirmOnSuccess) {
        setSubmitted(true);
        window.setTimeout(() => handleClose(false), 2500);
      } else {
        toast.success("Thanks! Our support team will get in touch within 24 hours.");
        handleClose(false);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to send your request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resolvedTitle = title ?? (planName ? `Contact us about the ${planName} plan` : "Contact Us");

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="overflow-hidden rounded-2xl border-border bg-card p-0 shadow-xl sm:max-w-[520px]">
        {submitted ? (
          <div className="flex flex-col items-center justify-center gap-4 px-8 py-16 text-center">
            <CheckCircle className="h-12 w-12 text-primary" />
            <p className="text-lg font-semibold text-foreground">
              ORbiT8 Team will reach out to you.
            </p>
          </div>
        ) : (
          <>
            <DialogHeader className="px-6 pb-2 pt-6">
              <DialogTitle className="text-xl font-semibold text-foreground">
                {resolvedTitle}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-5 px-6 pb-6">
              <div className="space-y-1.5">
                <Label htmlFor="contact-sales-full-name" className="font-medium text-foreground">
                  Full Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="contact-sales-full-name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Enter your full name"
                  required
                />
              </div>

              {showOrganisation && (
                <div className="space-y-1.5">
                  <Label htmlFor="contact-sales-organisation-name" className="font-medium text-foreground">
                    Organisation Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="contact-sales-organisation-name"
                    value={organisationName}
                    onChange={(e) => setOrganisationName(e.target.value)}
                    placeholder="Your company or organisation"
                    required
                  />
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="contact-sales-work-email" className="font-medium text-foreground">
                  Work Email <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="contact-sales-work-email"
                  type="email"
                  value={workEmail}
                  onChange={(e) => setWorkEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="contact-sales-area-of-interest" className="font-medium text-foreground">
                  Area of Interest <span className="text-destructive">*</span>
                </Label>
                <Select value={areaOfInterest} onValueChange={setAreaOfInterest}>
                  <SelectTrigger id="contact-sales-area-of-interest">
                    <SelectValue placeholder="Select area of interest" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTACT_SALES_AREAS_OF_INTEREST.map((area) => (
                      <SelectItem key={area} value={area}>
                        {area}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="contact-sales-message" className="font-medium text-foreground">
                  Message / Requirements
                </Label>
                <Textarea
                  id="contact-sales-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  placeholder="Tell us about team size, deployment needs, or features you want covered"
                />
              </div>

              <DialogFooter className="gap-2 pt-2 sm:gap-2">
                <Button type="button" variant="ghost" onClick={() => handleClose(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Sending..." : "Send request"}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContactSalesModal;
